import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/confirm-close-register.css';
import { useLocale } from '../../context';
import { useSiteContent } from '../../context/SiteContentContext';
import { CmsBreakText } from '../../utils/cmsBreakText';

const STORAGE_KEY = 'confirmCloseRegistration';

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M.456.454a1.094 1.094 0 0 1 1.548 0L9 7.468 16 .454A1.094 1.094 0 0 1 17.546 2l-7.014 6.998 7.015 6.998a1.087 1.087 0 0 1 0 1.545l-.002.003a1.09 1.09 0 0 1-1.545 0l-6.999-7.016-6.998 7.015v.001a1.09 1.09 0 0 1-1.545 0l-.003-.003a1.087 1.087 0 0 1 0-1.544l.001-.001 7.015-6.998L.456 2a1.094 1.094 0 0 1 0-1.547"
      />
    </svg>
  );
}

function pickLocale(obj, locale, key, fallback = '') {
  if (!obj) return fallback;
  const enKey = `${key}En`;
  if (locale === 'en') return obj[enKey] || obj[key] || fallback;
  return obj[key] || obj[enKey] || fallback;
}

export function hasConfirmedCloseRegistration() {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}

export function markConfirmedCloseRegistration() {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isConfirmCloseEnabled(register) {
  const cc = register?.confirmClose;
  if (cc && typeof cc.enabled === 'boolean') return cc.enabled;
  return true;
}

export default function ConfirmCloseRegisterModal({ open, onStay, onLeave }) {
  const { t, locale } = useLocale();
  const { register } = useSiteContent();
  const [entered, setEntered] = useState(false);
  const cc = register?.confirmClose || {};
  const banner = register?.text || {};

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onLeave?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onLeave]);

  if (!open) return null;

  const heading = pickLocale(
    cc,
    locale,
    'title',
    locale === 'en' ? 'Leaving Already?' : locale === 'ru' ? 'Уже уходите?' : 'Zaten ayrılıyor musun?',
  );
  const offerLead = pickLocale(
    cc,
    locale,
    'lead',
    locale === 'en' ? 'Your offer awaits.' : locale === 'ru' ? 'Ваше предложение ждёт.' : 'Teklifin seni bekliyor.',
  );
  const highlight = pickLocale(cc, locale, 'highlight', pickLocale(banner, locale, 'highlight', locale === 'en' ? '20.000 TRY' : '20.000 TL'));
  const offerLine = pickLocale(cc, locale, 'offer', pickLocale(banner, locale, 'title', locale === 'en' ? 'BONUS +100 FREE SPINS' : 'BONUS +300 FREESPIN'));
  const descLine = pickLocale(cc, locale, 'desc', pickLocale(banner, locale, 'desc', locale === 'en' ? 'WELCOME BONUS' : 'HOŞ GELDİN BONUSU'));
  const body = pickLocale(
    cc,
    locale,
    'body',
    locale === 'en'
      ? "Don't miss your chance to unlock these rewards and start playing today!"
      : locale === 'ru'
        ? 'Не упустите шанс получить награды и начать играть сегодня!'
        : 'Bu ödülleri kaçırma — hemen oynamaya başla!',
  );
  const claimLabel = pickLocale(
    cc,
    locale,
    'cta',
    locale === 'en' ? 'Claim Offer' : locale === 'ru' ? 'Забрать предложение' : 'Teklifi Al',
  );
  const imageUrl = cc.image || '/uploads/confirm-close-banner.webp';

  const offerBlock = [offerLead, [highlight, offerLine].filter(Boolean).join(' + '), descLine]
    .filter((line) => String(line || '').trim())
    .join('\n');

  return createPortal(
    <div
      id="sure-modal-register"
      className={`sure-modal sure-modal--register${entered ? ' is-entered' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-close-modal-title"
    >
      <div className="sure-modal__backdrop" onClick={onLeave} style={{ transitionDuration: '300ms' }} />
      <div className="sure-modal__content-wrapper">
        <div className="sure-modal__content" style={{ transitionDuration: '300ms' }}>
          <button
            type="button"
            id="sure-modal-register-close"
            className="sure-modal__close-button"
            onClick={onLeave}
            aria-label={t('common.close')}
          >
            <IconClose />
          </button>
          <div id="confirm-close-modal">
            <div className="modal-content__data">
              <div id="confirm-close-modal-title" className="modal-content__title">
                <CmsBreakText text={heading} />
              </div>
              <div className="modal-content__image-wrp">
                <img
                  className="modal-content__image image"
                  src={imageUrl}
                  alt=""
                  width={452}
                  height={240}
                  loading="eager"
                />
              </div>
              <div className="modal-content__content">
                <h3>
                  <mark>
                    <CmsBreakText text={offerBlock} />
                  </mark>
                </h3>
                <p>
                  <CmsBreakText text={body} />
                </p>
              </div>
              <div className="modal-content__btn-wrap">
                <div className="modal-content__btn-cell">
                  <button
                    type="button"
                    id="confirm-register-modal-btn-cancel"
                    className="modal-content__btn"
                    onClick={onStay}
                  >
                    {claimLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
